import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import appSettings from '@/config/index';
import { handleRequestError } from './interceptors/request/handleRequestError';
import { queueDuringAuthInit } from './interceptors/request/queueDuringAuthInit';
import { logSuccessfulRequest } from './interceptors/response/logSuccessfulRequest';
import { handleResponseError } from './interceptors/response/handleResponseError';
import { ApiClient } from './ApiClient';

export class ApiClientFactory {
  static create(baseUrl?: string, timeout?: number): ApiClient {
    const axiosInstance = this.createAxiosInstance(baseUrl, timeout);
    this.configureInterceptors(axiosInstance);
    return new ApiClient(axiosInstance, baseUrl ?? appSettings.api.baseUrl);
  }

  private static createAxiosInstance(baseUrl?: string, timeout?: number): AxiosInstance {
    return axios.create({
      baseURL: baseUrl ?? appSettings.api.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'Web',
      },
      timeout: timeout ?? appSettings.api.timeout,
    });
  }

  private static configureInterceptors(axiosInstance: AxiosInstance): void {
    axiosInstance.interceptors.request.use(
      async (config) => queueDuringAuthInit(config) as Promise<InternalAxiosRequestConfig>,
      (error: AxiosError) => handleRequestError(error)
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        logSuccessfulRequest(response);
        return response;
      },
      async (error: AxiosError<{ message?: string }>) => handleResponseError(error, axiosInstance)
    );
  }
}
